import { Fragment, useMemo, useState } from "react";
import Alert from "react-bootstrap/Alert";
import { v4 as uuidv4 } from "uuid";
import Button from "react-bootstrap/Button";
import { PolicyDecisionActionTypes } from "@iddataweb/idw-common-ui/enums";
import { resources } from "@constants/hardcoded-strings";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { POLICY_DECISION_ACTION_TYPE_OPTIONS } from "@constants/policy-decision-action-type-options";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { LayoutTypes } from "@enums/layout-types";
import { GenericReferenceDataTypes } from "@enums/generic-reference-data-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { PolicyDecisionOperationTypes } from "@enums/policy-decision-operation-types";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { AxnRpService } from "@idw-types/data/axn-rp-service";
import { SelectOption } from "@idw-types/common";
import {
    FormattedRpServiceConfigurationAttribute,
    FormattedRpServiceConfigurationPolicyConfigDecision,
    FormattedRpServiceConfigurationPolicyConfigDecisionCondition,
    FormattedRpServiceConfigurationServiceOffering,
    FormattedRpServiceConfigurationServiceOfferingAssertion
} from "@idw-types/data/rp-service-configuration";
import { AxnAttributeType, AxnAttributeTypeField, AxnCompany } from "@idw-types/data/reference-data/axn"
import { AxnAcquiredAttribute } from "@idw-types/data/reference-data/axn/axn-acquired-attribute";
import { getAxnRpServiceOptions, getComponentsToDisplay, getObligationStepUpToList, isObligationApiKeyCorrect } from "@utils/policies/policy-util";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { SelectFormGroup } from "../../common/select-form-group/select-form-group";
import { IdwFormLabel } from "../idw-form-label/idw-form-label";
import { RadioInputFormGroup } from "../radio-input-form-group/radio-input-form-group";
import { PolicyDecisionConditionContainer } from "./policy-decision-condition-container/policy-decision-condition-container";
import { PolicyDecisionOperationContainer } from "./policy-decision-operation-container/policy-decision-operation-container";
import styles from "./rp-service-configuration-policy-decision-container.less";
import { AvailableAssertionStatus } from "@idw-types/data/reference-data/generic";
import { AXNUIContent } from "@iddataweb/idw-common-ui/types";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { isNullOrUndefined } from "@utils/object-util";
import { PolicyDetailErrors } from "@idw-types/common/validation/verification-service-details-validation-errors";
import { SimpleRPServiceWorkflow } from "@idw-types/data/reference-data/axn/simple-rp-service-workflow";

type RpServiceConfigurationPolicyDecisionContainerProps = {
    isDisabled: boolean;
    isUsingThemedUI: boolean;
    axnCompanies: Array<AxnCompany>;
    axnRpServices: Array<AxnRpService>;
    unformattedAcquiredAttributes: Array<AxnAcquiredAttribute>;
    attributes: Array<FormattedRpServiceConfigurationAttribute>;
    axnuiContent: AXNUIContent;
    acquiredAttributes: Array<FormattedRpServiceConfigurationAttribute>;
    decision: FormattedRpServiceConfigurationPolicyConfigDecision;
    policyRetries: string;
    validationErrors: PolicyDetailErrors;
    axnAttributeTypes: Array<AxnAttributeType>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    serviceOfferings: Array<FormattedRpServiceConfigurationServiceOffering>;
    simpleRPServiceWorkflows: Array<SimpleRPServiceWorkflow>;
    axnAvailableAssertionStatuses: Array<AvailableAssertionStatus>;
    rpServiceID: string;
    workflowID: string;
    updateAXNUIContentPolicyDecisionRetryMessage: (decisionUuid: string, retryMessage: string) => void;
    setDecision: (decision: FormattedRpServiceConfigurationPolicyConfigDecision) => void;
};

export const RpServiceConfigurationPolicyDecisionContainer = (props: RpServiceConfigurationPolicyDecisionContainerProps) => {
    const {
        isDisabled,
        isUsingThemedUI,
        axnCompanies,
        axnRpServices,
        unformattedAcquiredAttributes,
        decision,
        validationErrors,
        policyRetries,
        attributes,
        axnuiContent,
        acquiredAttributes,
        serviceOfferings,
        axnAttributeTypes,
        axnAttributeTypeFields,
        simpleRPServiceWorkflows,
        rpServiceID,
        workflowID,
        axnAvailableAssertionStatuses,
        updateAXNUIContentPolicyDecisionRetryMessage,
        setDecision
    } = props;

    const {
        deployedPolicyComponents,
        simpleDeployedPolicyComponentConditions
    } = useReferenceDataSelector([
        GenericReferenceDataTypes.DEPLOYED_POLICY_COMPONENT,
        GenericReferenceDataTypes.SIMPLE_DEPLOYED_POLICY_COMPONENT_CONDITION
    ]);

    const retryMessage = useMemo(() => {
        if (decision.id && !isNullOrUndefined(axnuiContent.policyDecisionRetryMessage[decision.id]?.retryMessage)) {
            return axnuiContent.policyDecisionRetryMessage[decision.id].retryMessage;
        } else if (!isNullOrUndefined(axnuiContent.policyDecisionRetryMessage[decision.uuid])) {
            return axnuiContent.policyDecisionRetryMessage[decision.uuid].retryMessage;
        } else {
            return axnuiContent.policyDecisionRetryMessage.default.retryMessage;
        }
    }, [decision, axnuiContent]);

    const [showAllComponents, setShowAllComponents] = useState(false);

    const handleSetShowAllComponents = (newValue: boolean) => {
        setShowAllComponents(newValue);
        // todo - need to clear component type filter somehow...
    };

    const handlePolicyDecisionActionTypeChange = (newValue: SelectOption) => {
        if (newValue.value === PolicyDecisionActionTypes.DENY && isNullOrUndefined(axnuiContent.policyDecisionRetryMessage[decision.id]?.retryMessage) &&
            isNullOrUndefined(axnuiContent.policyDecisionRetryMessage[decision.uuid]?.retryMessage)) {
            updateAXNUIContentPolicyDecisionRetryMessage(decision.uuid, axnuiContent.policyDecisionRetryMessage.default.retryMessage);
        }
        setDecision({
            ...decision,
            policyDecisionType: newValue.value as PolicyDecisionActionTypes,
            obligationValue: "",
            retries: newValue.value === PolicyDecisionActionTypes.APPROVE ? "0" : policyRetries
        });
    };

    const handlePolicyDecisionOperationTypeChange = (newValue: SelectOption) => {
        setDecision({
            ...decision,
            policyDecisionOperationType: newValue.value as PolicyDecisionOperationTypes
        });
    };

    const handleStepUpApiKeyChange = (newValue: SelectOption) => {
        setDecision({
            ...decision,
            obligationValue: newValue.value
        });
    };

    const handleInlineRetryChange = (newValue: string) => {
        setDecision({
            ...decision,
            retries: isStringTrue(newValue) ? policyRetries : "0"
        });
    };

    const handleContentPolicyDecisionRetryMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (decision.id) {
            updateAXNUIContentPolicyDecisionRetryMessage(decision.id, event.target.value);
        }
        else {
            updateAXNUIContentPolicyDecisionRetryMessage(decision.uuid, event.target.value);
        }
    };

    const handleAddConditionClick = () => {
        const newConditions: Array<FormattedRpServiceConfigurationPolicyConfigDecisionCondition> = [...decision.conditions];
        newConditions.push({
            assertionID: null,
            attributeField: null,
            component: null,
            description: null,
            id: null,
            policyConditionOperator: null,
            result: null,
            serviceOfferingID: null,
            values: null,
            uuid: uuidv4()
        });
        setDecision({
            ...decision,
            conditions: newConditions
        });
    };

    const handleUpdateCondition = (conditionToBeUpdated: FormattedRpServiceConfigurationPolicyConfigDecisionCondition) => {
        const newConditions = decision.conditions.map(condition => condition.uuid === conditionToBeUpdated.uuid ? conditionToBeUpdated : condition);
        setDecision({
            ...decision,
            conditions: newConditions
        });
    };

    const handleRemoveCondition = (conditionToBeRemoved: FormattedRpServiceConfigurationPolicyConfigDecisionCondition) => {
        const newConditions = decision.conditions.filter(condition => condition.uuid !== conditionToBeRemoved.uuid);
        setDecision({
            ...decision,
            conditions: newConditions
        });
    };

    const obligationStepUpList = useMemo(() => {
        if (axnRpServices.length > 0) {
            return getObligationStepUpToList(rpServiceID, workflowID, axnRpServices, simpleRPServiceWorkflows);
        }

        return [];
    }, [axnRpServices, rpServiceID, workflowID, simpleRPServiceWorkflows]);

    const axnRpServiceOptions = useMemo(() => {
        return getAxnRpServiceOptions(obligationStepUpList, decision.obligationValue, axnRpServices);

    }, [obligationStepUpList, decision.obligationValue]);

    const selectedPolicyDecisionActionType = useMemo(() => {
        return POLICY_DECISION_ACTION_TYPE_OPTIONS.find(option => option.value === decision.policyDecisionType) || null;
    }, [decision.policyDecisionType]);

    const selectedStepUpApiKey = useMemo(() => {
        if (decision.obligationValue) {
            const axnRpServiceOption = axnRpServiceOptions.find(option => option.value === decision.obligationValue);
            if (axnRpServiceOption) {
                return axnRpServiceOption;
            }
        }
        return null;
    }, [axnRpServiceOptions, decision.obligationValue]);


    const shouldShowObligationWarningMessage = useMemo(() => {
        if (obligationStepUpList.length && decision.obligationValue) {
            return !isObligationApiKeyCorrect(decision.obligationValue, obligationStepUpList);
        }

        return false;
    }, [obligationStepUpList, decision.obligationValue]);

    const availableAttributes = useMemo(() => {
        return attributes.filter(attr =>
            attr.addedToService &&
            attr.status === EntityStatusTypes.ACTIVE
        );
    }, [attributes]);

    const assertions = useMemo(() => {
        const allAssertions: Array<FormattedRpServiceConfigurationServiceOfferingAssertion> = [];
        serviceOfferings.forEach(so => {
            so.assertions.forEach(assertion => {
                allAssertions.push(assertion);
            });
        });
        return allAssertions;
    }, [serviceOfferings]);

    const availableAssertions = useMemo(() => {
        const availableAssertions: Array<FormattedRpServiceConfigurationServiceOfferingAssertion> = [];
        serviceOfferings.filter(so => so.addedToService && so.status === EntityStatusTypes.ACTIVE).forEach(so => {
            so.assertions.forEach(assertion => {
                if (
                    assertion.addedToService &&
                    assertion.status === EntityStatusTypes.ACTIVE &&
                    assertion.apAssertionStatus === EntityStatusTypes.ACTIVE) {
                    availableAssertions.push(assertion);
                }
            });
        });
        return availableAssertions;
    }, [serviceOfferings]);

    const availableDeployedPolicyComponents = useMemo(() => {
        const activeComponents = deployedPolicyComponents.filter(component => (
            component.status === EntityStatusTypes.ACTIVE &&
            component.type !== "LIST"
        ));

        const availableAxnAttributeTypeFieldMap: { [key: string]: AxnAttributeTypeField } = {};
        axnAttributeTypeFields.forEach(atf => {
            availableAxnAttributeTypeFieldMap[atf.attributeTypeID] = atf;
        });

        const availableAttributesWithFieldIds: Array<FormattedRpServiceConfigurationAttribute & { fieldId: string }> = [];

        [...availableAttributes, ...acquiredAttributes].forEach(attr => {
            if (availableAxnAttributeTypeFieldMap[attr.attributeType.id]) {
                availableAttributesWithFieldIds.push({
                    ...attr,
                    fieldId: availableAxnAttributeTypeFieldMap[attr.attributeType.id].id
                });
            }
        });

        const filteredComponents = getComponentsToDisplay(
            activeComponents,
            simpleDeployedPolicyComponentConditions,
            availableAssertions,
            availableAttributesWithFieldIds
        );

        return filteredComponents;

    }, [
        deployedPolicyComponents,
        simpleDeployedPolicyComponentConditions,
        availableAssertions,
        axnAttributeTypeFields,
        availableAttributes,
        acquiredAttributes
    ]);

    const unselectedDeployedPolicyComponents = useMemo(() => {
        const selectedComponentIds = (decision.conditions || []).map(condition => condition.component?.id).filter(x => x);

        if (showAllComponents) {
            return deployedPolicyComponents;
        } else {
            const availableComponents = availableDeployedPolicyComponents.filter(component => (
                !selectedComponentIds.includes(component.id)
            ));

            return availableComponents;
        }
    }, [
        decision.conditions,
        acquiredAttributes,
        availableAttributes,
        availableAssertions,
        axnAttributeTypeFields,
        simpleDeployedPolicyComponentConditions,
        availableDeployedPolicyComponents,
        showAllComponents
    ]);

    const selectedAttributeIds = useMemo(() => {
        return (decision.conditions || []).map(condition => condition.attributeField?.id).filter(x => x);
    }, [decision.conditions]);

    const unselectedAxnAttributeTypeFields = useMemo(() => {
        const availableAttributeTypeIds = availableAttributes.map(attr => attr.attributeType.id);

        return axnAttributeTypeFields.filter(atf => !selectedAttributeIds.includes(atf.id) && availableAttributeTypeIds.includes(atf.attributeTypeID));
    }, [attributes, selectedAttributeIds]);

    const unselectedAxnAcquiredAttributeTypeFields = useMemo(() => {
        const availableAcquiredAttributeTypeIds = acquiredAttributes.map(attr => attr.attributeType.id);
        return axnAttributeTypeFields.filter(atf => !selectedAttributeIds.includes(atf.id) && availableAcquiredAttributeTypeIds.includes(atf.attributeTypeID));
    }, [axnAttributeTypeFields, acquiredAttributes, selectedAttributeIds]);

    return (
        <div className={styles.rpServiceConfigurationPolicyDecisionContainer}>
            <div className={styles.conditionsConfigurationContainer}>
                <IdwFormLabel className={styles.resultLabel} isRequired={true} label={"If the result of:"} labelWidth={LabelWidthTypes.SMALL} />
                <div className={styles.conditionsContainer}>
                    {decision.conditions.length > 0 && (
                        decision.conditions.map((condition, index) => (
                            <Fragment key={`policy-decision-condition-${condition.uuid}`}>
                                <PolicyDecisionConditionContainer
                                    isDisabled={isDisabled}
                                    condition={condition}
                                    axnCompanies={axnCompanies}
                                    allDeployedPolicyComponents={deployedPolicyComponents}
                                    unselectedDeployedPolicyComponents={unselectedDeployedPolicyComponents}
                                    unselectedAxnAttributeTypeFields={unselectedAxnAttributeTypeFields}
                                    unselectedAxnAcquiredAttributeTypeFields={unselectedAxnAcquiredAttributeTypeFields}
                                    availableDeployedPolicyComponents={availableDeployedPolicyComponents}
                                    deployedPolicyComponentConditions={simpleDeployedPolicyComponentConditions}
                                    assertions={assertions}
                                    availableAssertions={availableAssertions}
                                    serviceOfferings={serviceOfferings}
                                    selectedRpServiceConfigurationAttributes={availableAttributes}
                                    axnAttributeTypes={axnAttributeTypes}
                                    allAxnAttributeTypeFields={axnAttributeTypeFields}
                                    axnAttributeTypeFields={unselectedAxnAttributeTypeFields}
                                    axnAcquiredAttributeTypeFields={unselectedAxnAcquiredAttributeTypeFields}
                                    showAllComponents={showAllComponents}
                                    acquiredAttributes={unformattedAcquiredAttributes}
                                    axnAvailableAssertionStatuses={axnAvailableAssertionStatuses}
                                    setShowAllComponents={handleSetShowAllComponents}
                                    updateCondition={handleUpdateCondition}
                                    removeCondition={handleRemoveCondition}
                                />
                                {decision.conditions.length > 1 && index !== decision.conditions.length - 1 && (
                                    <PolicyDecisionOperationContainer
                                        index={index}
                                        isDisabled={isDisabled}
                                        policyDecisionOperationType={decision.policyDecisionOperationType}
                                        handlePolicyDecisionOperationTypeChange={handlePolicyDecisionOperationTypeChange}
                                    />
                                )}
                            </Fragment>
                        ))
                    )}
                    <div className={styles.conditionsHeaderContainer}>
                        {decision.conditions.length === 0 && (
                            <IdwFormLabel isRequired={false} label={`No conditions have been added. ${isDisabled ? "" : "Click Add Condition to add one now."}`} labelWidth={LabelWidthTypes.EXTRA_SMALL} />
                        )}
                        {!isDisabled && (
                            <Button id={`policy-config-decision-${decision.uuid}-add-condition`} onClick={handleAddConditionClick}>Add Condition</Button>
                        )}
                    </div>
                </div>
            </div>

            <SelectFormGroup
                controlId={`policy-config-decision-${decision.uuid}-select-input`}
                automationId={`policy-config-decision-${decision.uuid}-select-input`}
                label={resources.modals.addRpServiceConfigurationPolicyConfigDecision.inputs.decisionType.label}
                ariaLabel={resources.modals.addRpServiceConfigurationPolicyConfigDecision.inputs.decisionType.label}
                disabled={isDisabled}
                isClearable={false}
                isRequired={true}
                isSearchable={false}
                layout={LayoutTypes.ROW}
                labelWidth={LabelWidthTypes.SMALL}
                onChange={handlePolicyDecisionActionTypeChange}
                options={POLICY_DECISION_ACTION_TYPE_OPTIONS}
                selectedOption={selectedPolicyDecisionActionType}
                afterLabelTooltipText={"Select the action to perform when the conditions above are met"}
            />
            {decision.policyDecisionType === PolicyDecisionActionTypes.OBLIGATION && (
                <> <SelectFormGroup
                    controlId={`policy-config-decision-${decision.uuid}-step-up-api-key-select-input`}
                    automationId={`policy-config-decision-${decision.uuid}-step-up-api-key-select-input`}
                    label={resources.modals.addRpServiceConfigurationPolicyConfigDecision.inputs.stepUpTo.label}
                    ariaLabel={resources.modals.addRpServiceConfigurationPolicyConfigDecision.inputs.stepUpTo.label}
                    disabled={isDisabled}
                    isClearable={false}
                    isRequired={true}
                    isSearchable={true}
                    layout={LayoutTypes.ROW}
                    labelWidth={LabelWidthTypes.SMALL}
                    onChange={handleStepUpApiKeyChange}
                    options={axnRpServiceOptions}
                    selectedOption={selectedStepUpApiKey}
                    afterLabelTooltipText={"Select the service to step up to when the conditions above are met"}
                />
                    {shouldShowObligationWarningMessage && <Alert variant={"warning"}>{resources.pages.verificationServiceDetails.tabs.policies.sections.details.obligationWarningMessage}</Alert>}
                </>
            )}
            {[PolicyDecisionActionTypes.OBLIGATION, PolicyDecisionActionTypes.DENY].includes(decision.policyDecisionType) && (
                <RadioInputFormGroup
                    controlId={`policy-config-decision-${decision.uuid}-inline-retry-radio-input`}
                    automationId={`policy-config-decision-${decision.uuid}-inline-retry-radio-input`}
                    disabled={isDisabled}
                    isRequired={true}
                    label={"Inline Retry"}
                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                    onChange={handleInlineRetryChange}
                    value={decision.retries === "0" ? "false" : "true"}
                    afterLabelTooltipText={"Allows the user to retry the verification attempt when the conditions above are met and the action is set to Deny or Obligation"}
                />
            )}
            {decision.policyDecisionType === PolicyDecisionActionTypes.DENY && isUsingThemedUI && decision.retries !== "0" && (
                <><InputFormGroup
                    controlId={"policy-config-rule-retry-message-input"}
                    automationId={"policy-config-rule-retry-message-input"}
                    disabled={isDisabled}
                    isRequired={true}
                    label={"Retry Message"}
                    ariaLabel={"Retry Message"}
                    type={"text"}
                    value={retryMessage}
                    onChange={handleContentPolicyDecisionRetryMessageChange} />
                    {validationErrors.policyConfigDecisionsRetryErrors[decision.id] || validationErrors.policyConfigDecisionsRetryErrors[decision.uuid] || null}
                </>
            )}
        </div>
    );
};
